/*
  Holds checkout while the cart has something to say.

  `cart-eligibility.liquid` renders the notices; this element wraps the
  checkout button and, when notices exist, shows them in a dialog before the
  form is submitted. An error notice blocks checkout outright, a warning is
  acknowledged and the original submit goes ahead.

  The dialog is created once, appended to <body> and populated with *clones* of
  the notices, so the source markup survives and the dialog opens as many times
  as the shopper clicks.
*/

if (!customElements.get('cart-checkout-guard')) {
  class CartCheckoutGuard extends HTMLElement {
    static dialog = null;

    connectedCallback() {
      this.addEventListener('click', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
    }

    handleEvent(event) {
      const trigger = event.target.closest('[name="checkout"]');
      if (!trigger) return;

      const notices = this.notices();
      if (notices.length === 0) return;

      event.preventDefault();
      this.open(notices, trigger);
    }

    /* Notices belong to the cart this button sits in — the drawer and the cart
       page each render their own. */
    notices() {
      const scope = this.closest('[data-cart-scope]') || document;
      const list = scope.querySelector('[data-cart-messages]');
      return list ? Array.from(list.querySelectorAll('.cart-message')) : [];
    }

    open(notices, trigger) {
      const dialog = this.constructor.ensureDialog();
      const body = dialog.querySelector('[data-cart-guard-body]');
      const proceed = dialog.querySelector('[data-cart-guard-proceed]');

      body.replaceChildren(...notices.map((notice) => notice.cloneNode(true)));

      /* An error is not something the shopper can click past. */
      const blocked = notices.some((notice) => notice.classList.contains('cart-message--error'));
      proceed.hidden = blocked;

      dialog.__submitter = blocked ? null : trigger;

      /* The theme's colours live on .color-<scheme> wrappers. The dialog is
         appended to <body>, outside all of them, so it has to carry the scheme
         of the cart it was opened from or its buttons resolve to nothing. */
      dialog.className = `cart-guard ${this.constructor.schemeOf(this)}`;

      dialog.showModal();
    }

    static schemeOf(element) {
      for (let node = element; node; node = node.parentElement) {
        const scheme = Array.from(node.classList).find((name) => name.startsWith('color-'));
        if (scheme) return scheme;
      }
      return 'color-scheme-1';
    }

    static ensureDialog() {
      if (this.dialog && this.dialog.isConnected) return this.dialog;

      const dialog = document.createElement('dialog');
      dialog.className = 'cart-guard';
      dialog.innerHTML = `
        <div class="cart-guard__panel">
          <div class="cart-guard__body" data-cart-guard-body></div>
          <div class="cart-guard__actions">
            <button type="button" class="button button--filled button--full cart-guard__proceed" data-cart-guard-proceed>
              Proceed to checkout
            </button>
            <button type="button" class="cart-guard__dismiss" data-cart-guard-dismiss>
              Back to cart
            </button>
          </div>
        </div>
      `;

      dialog.addEventListener('click', (event) => {
        /* The backdrop is the dialog itself; the panel stops the click. */
        if (event.target === dialog) {
          dialog.close();
          return;
        }

        if (event.target.closest('[data-cart-guard-dismiss]')) {
          dialog.close();
          return;
        }

        if (event.target.closest('[data-cart-guard-proceed]')) {
          const submitter = dialog.__submitter;
          dialog.close();
          if (!submitter) return;

          /* requestSubmit keeps the button's name and still enforces the
             terms checkbox, which a bare redirect to /checkout would skip. */
          const form = submitter.form || submitter.closest('form');
          if (form) {
            form.requestSubmit(submitter);
          } else {
            window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`.replace('//', '/');
          }
        }
      });

      document.body.appendChild(dialog);
      this.dialog = dialog;
      return dialog;
    }
  }

  customElements.define('cart-checkout-guard', CartCheckoutGuard);
}
